import { Command } from 'commander';
import { promises as fs } from 'fs';
import { join } from 'path';
import { checkbox, confirm, Separator } from '@inquirer/prompts';
import { ValidationError } from '../../errors/index.js';
import { RulesGeneratorService } from '../../services/rules-generator.service.js';
import { ConfigService } from '../../services/config.service.js';
import { ProjectConfigService, type RulesConfig } from '../../services/project-config.service.js';
import { IdeDetectionService } from '../../services/ide-detection.service.js';
import { IDE_FORMATS } from '../../config/ide-formats.js';
import { checkboxTheme } from '../../utils/prompt-theme.js';
import { MigrateIdeService } from '../../services/migrate-ide.service.js';

interface PresetBundle {
    preset: {
        slug: string;
        name: string;
        description: string;
        version: string;
        tags: string[];
        stack: Record<string, string | null>;
        files: {
            required: string[];
            optional: string[];
            template: string[];
        };
        output?: {
            agentsMd?: boolean;
            ideFolder?: string;
        };
    };
    files: Record<string, string>;
}

export function createRulesSyncCommand(): Command {
    return new Command('sync')
        .description('Regenerate rule outputs for all configured IDEs')
        .option('--ides <ides>', 'Comma-separated list of IDEs to sync (default: all configured)')
        .option('--detect', 'Auto-detect active IDEs instead of using config')
        .option('-y, --yes', 'Skip confirmations')
        .action(async (options) => {
            // Check for rule preset first (source of truth)
            const rulePresetDir = join(process.cwd(), '.jai1', 'rule-preset');
            const presetJsonPath = join(rulePresetDir, 'preset.json');

            let presetExists = false;
            let presetData: any = null;

            try {
                const presetContent = await fs.readFile(presetJsonPath, 'utf-8');
                presetData = JSON.parse(presetContent);
                presetExists = true;
            } catch {
                // No preset found
            }

            if (!presetExists) {
                throw new ValidationError(
                    'No rule preset found in .jai1/rule-preset/\n' +
                    'Run "jai1 rules apply <preset>" or create a project with "jai1 kit create <kit>" first.'
                );
            }

            // Load project config or create from preset
            const projectConfigService = new ProjectConfigService();
            let rulesConfig = await projectConfigService.loadRules();

            if (!rulesConfig) {
                // Create config from preset.json
                console.log('⚠️  No .jai1/project.json found, creating from preset...\n');
                rulesConfig = {
                    preset: presetData.slug,
                    version: presetData.version,
                    appliedAt: new Date().toISOString(),
                    ides: [],
                    customContext: '09-custom.md',
                    backups: [],
                };
            }

            console.log('🔄 Syncing rules...\n');
            console.log(`Preset: ${rulesConfig.preset} v${rulesConfig.version}`);

            // Determine which IDEs to sync
            let idesToSync: string[] = [];

            // Option 1: Auto-detect active IDEs
            if (options.detect) {
                console.log('\n🔍 Auto-detecting active IDEs...');
                const detectionService = new IdeDetectionService();
                const detected = await detectionService.detectActiveIdes();

                if (detected.length === 0) {
                    throw new ValidationError(
                        'No active IDEs detected. Run "jai1 rules apply" first or use "jai1 ide status" to check.'
                    );
                }

                console.log(`\nDetected ${detected.length} active IDE(s):\n`);
                detected.forEach(d => {
                    const confidence = d.confidence === 'high' ? '🟢' : d.confidence === 'medium' ? '🟡' : '🔴';
                    console.log(`  ${confidence} ${d.name} - ${d.ruleCount} rules`);
                });

                if (!options.yes) {
                    const proceed = await confirm({
                        message: '\nSync these detected IDEs?',
                        default: true,
                    });

                    if (!proceed) {
                        console.log('Cancelled.');
                        return;
                    }
                }

                idesToSync = detected.map(d => d.id);
            }
            // Option 2: Specific IDEs via flag
            else if (options.ides) {
                const requested = (options.ides as string).split(',').map(ide => ide.trim());
                const configured = rulesConfig.ides || [];

                // Use requested IDEs, but warn if not in config
                idesToSync = requested;
                const notConfigured = requested.filter(ide => !configured.includes(ide));

                if (notConfigured.length > 0) {
                    console.log(`\n⚠️  IDEs not in config: ${notConfigured.join(', ')}`);
                    console.log('   They will still be synced, but may not be in .jai1/project.json');
                }
            }
            // Option 3: Interactive selection (default when no flags, unless --yes)
            else if (!options.yes) {
                const currentIdes = rulesConfig.ides || [];

                // Show current configuration
                console.log(`\nCurrent IDE(s): ${currentIdes.join(', ') || 'none'}`);

                // Detect existing IDE files and get suggestions
                const detectionService = new IdeDetectionService();
                const detected = await detectionService.detectActiveIdes();
                const suggestions = await detectionService.suggestIdes();

                // Show summary of current state
                if (detected.length > 0) {
                    const detectedCount = detected.reduce((sum, d) => sum + d.ruleCount + d.workflowCount, 0);
                    console.log(`  (${detectedCount} files detected)`);
                }

                // Show smart suggestions
                if (suggestions.length > 0) {
                    console.log('\n💡 Smart suggestions based on your project:\n');
                    suggestions.slice(0, 3).forEach(s => {
                        const priority = s.priority === 'high' ? '⭐' : s.priority === 'medium' ? '🔸' : '▫️';
                        console.log(`${priority} ${s.name} - ${s.reason}`);
                    });
                    console.log('');
                }

                // Build choices with smart pre-selection
                const choices = buildIdeChoices(currentIdes, detected, suggestions);

                // Show interactive prompt
                idesToSync = await checkbox({
                    message: 'Select IDE formats to sync:',
                    choices,
                    required: false,
                    theme: checkboxTheme,
                });

                // Handle quit option
                if (idesToSync.includes('__quit__')) {
                    console.log('Cancelled.');
                    return;
                }

                // If user cancelled or selected nothing
                if (idesToSync.length === 0) {
                    console.log('Cancelled.');
                    return;
                }
            }
            // Option 4: Auto mode with --yes flag
            else {
                idesToSync = rulesConfig.ides || [];

                // Legacy support: if no IDEs configured, detect from existing files
                if (idesToSync.length === 0) {
                    console.log('\n⚠️  No IDEs configured in .jai1/project.json');
                    console.log('   Detecting from existing files...\n');

                    const detectionService = new IdeDetectionService();
                    idesToSync = await detectionService.detectExistingIdes();

                    if (idesToSync.length === 0) {
                        throw new ValidationError(
                            'No rule outputs found. Run "jai1 rules apply" first.'
                        );
                    }

                    console.log(`   Found: ${idesToSync.join(', ')}\n`);
                }
            }

            console.log(`\n📝 Syncing ${idesToSync.length} IDE(s): ${idesToSync.join(', ')}\n`);

            // Fetch preset bundle to regenerate
            const configService = new ConfigService();
            const config = await configService.load();

            if (!config) {
                throw new ValidationError('Not initialized. Run "jai1 auth" first.');
            }

            const presetResponse = await fetch(`${config.apiUrl}/api/rules/presets/${rulesConfig.preset}`, {
                headers: {
                    'JAI1-Access-Key': config.accessKey,
                },
            });

            if (!presetResponse.ok) {
                throw new Error(`Failed to fetch preset: ${presetResponse.statusText}`);
            }

            const bundle = (await presetResponse.json()) as PresetBundle;

            // Read source files from .jai1/rule-preset/ (source of truth)
            // Update bundle with edited files from local preset
            const files = await fs.readdir(rulePresetDir);

            for (const file of files) {
                if (file.endsWith('.mdc') || file.endsWith('.md')) {
                    const filePath = join(rulePresetDir, file);
                    const content = await fs.readFile(filePath, 'utf-8');
                    bundle.files[file] = content;
                }
            }

            // Regenerate for each IDE
            const generatorService = new RulesGeneratorService();

            for (const ideId of idesToSync) {
                try {
                    const format = IDE_FORMATS[ideId];
                    if (!format) {
                        console.log(`⚠️  Unknown IDE format: ${ideId}, skipping`);
                        continue;
                    }

                    if (ideId === 'codex') {
                        const migrateService = new MigrateIdeService();
                        const content = await migrateService.scanJai1Content();
                        const results = await migrateService.migrate(['codex'], ['rules'], content);
                        console.log(`✓ ${format.name} - ${results.length} managed AGENTS.md block(s) regenerated`);
                        continue;
                    }

                    const files = generatorService.generateForIde(bundle, ideId);

                    for (const file of files) {
                        const fullPath = join(process.cwd(), file.path);
                        await fs.mkdir(join(fullPath, '..'), { recursive: true });
                        await fs.writeFile(fullPath, file.content, 'utf-8');
                    }

                    console.log(`✓ ${format.name} - ${files.length} files regenerated`);
                } catch (error) {
                    console.error(`✗ Failed to sync ${ideId}:`, error);
                }
            }

            // Update config with synced IDEs (if different)
            if (JSON.stringify(rulesConfig.ides) !== JSON.stringify(idesToSync)) {
                rulesConfig.ides = idesToSync;
                await projectConfigService.saveRules(rulesConfig);
                console.log('\n✓ Updated .jai1/project.json with synced IDEs');
            }

            console.log('\n✅ Rules synced successfully!\n');
            console.log('💡 Next steps:');
            console.log('   • Your IDE may need to be restarted to pick up changes');
            console.log('   • Use "jai1 ide status" to verify IDE configuration');
            console.log('   • Edit source files in .jai1/rule-preset/ and sync again\n');
        });
}

/**
 * Build IDE choices for checkbox prompt with smart pre-selection
 */
function buildIdeChoices(
    currentIdes: string[],
    detected: Array<{ id: string; name: string; detected: boolean }>,
    suggestions: Array<{ ideId: string; priority: string }>
): Array<{ name: string; value: string; checked: boolean } | Separator> {
    const choices: Array<{ name: string; value: string; checked: boolean } | Separator> = [
        {
            name: 'Cursor (.cursor/rules/)',
            value: 'cursor',
            checked: currentIdes.includes('cursor') || detected.some(d => d.id === 'cursor')
        },
        {
            name: 'Windsurf (.windsurf/rules/)',
            value: 'windsurf',
            checked: currentIdes.includes('windsurf') || detected.some(d => d.id === 'windsurf')
        },
        {
            name: 'Antigravity (.agent/rules/)',
            value: 'antigravity',
            checked: currentIdes.includes('antigravity') || detected.some(d => d.id === 'antigravity')
        },
        {
            name: 'Claude Code (.claude/rules/)',
            value: 'claude',
            checked: currentIdes.includes('claude') || detected.some(d => d.id === 'claude')
        },
        {
            name: 'Codex (AGENTS.md + .agents/skills/)',
            value: 'codex',
            checked: currentIdes.includes('codex') || detected.some(d => d.id === 'codex')
        },
        {
            name: 'AGENTS.md (single file)',
            value: 'agentsmd',
            checked: currentIdes.includes('agentsmd') || detected.some(d => d.id === 'agentsmd')
        },
        {
            name: 'Gemini CLI (GEMINI.md)',
            value: 'gemini',
            checked: currentIdes.includes('gemini') || detected.some(d => d.id === 'gemini')
        },
        new Separator(),
        {
            name: '✖ Quit',
            value: '__quit__',
            checked: false
        },
    ];

    return choices;
}

async function checkPathExists(absolutePath: string): Promise<boolean> {
    try {
        await fs.access(absolutePath);
        return true;
    } catch {
        return false;
    }
}
